import React, { useState, useEffect } from "react";
import Store from "../../../utils/Store";
import AwesomeIcon from "../../AwesomeIcon";
import {
  faXmark,
  faUserTag,
  faSearch,
  faMemoPad,
  faStar,
  faFileLines,
  faArrowsRotate,
  faCopy,
  faPaperPlane,
} from "@fortawesome/pro-regular-svg-icons";
import promptsData from "./prompts.json";

interface PromptsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Prompt {
  name: string;
  content: string;
}

interface Section {
  title: string;
  icon: string;
  prompts: Prompt[];
}

const iconMap: { [key: string]: any } = {
  faUserTag,
  faSearch,
  faMemoPad,
  faStar,
  faFileLines,
  faArrowsRotate,
};

const PromptsModal = ({ isOpen, onClose }: PromptsModalProps) => {
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const handleHashChange = () => {
      if (!isOpen) return;

      if (window.location.hash === "#prompts") {
        document.dispatchEvent(new CustomEvent("openPromptsModal"));
      } else {
        onClose();
      }
    };

    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [onClose]);

  const handleCopyPrompt = (content: string) => {
    navigator.clipboard.writeText(content);
  };

  const handleUsePrompt = (content: string) => {
    Store.update((s) => {
      s.chatInputContent = content;
    });
    handleClose();
  };

  const handleClose = () => {
    Store.update((s) => {
      s.isDraggableModalOpen = true;
    });
    window.location.hash = "";
    onClose();
  };

  const filteredSections = promptsData.sections
    .filter(
      (section) =>
        section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        section.prompts.some(
          (prompt) =>
            prompt.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            prompt.content.toLowerCase().includes(searchQuery.toLowerCase())
        )
    )
    .map((section) => ({
      ...section,
      prompts: section.title.toLowerCase().includes(searchQuery.toLowerCase())
        ? section.prompts // Show all prompts if section title matches
        : section.prompts.filter(
            (
              prompt // Filter prompts if only prompt content matches
            ) =>
              prompt.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
              prompt.content.toLowerCase().includes(searchQuery.toLowerCase())
          ),
    }));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-black opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-gray-900 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-gray-900 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-white">AI Assistant Prompts</h3>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <AwesomeIcon name={faXmark} size="lg" />
              </button>
            </div>

            <div className="mb-6">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search prompts or sections..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-3 pl-10 border border-gray-700 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:outline-none focus:border-teal-500"
                />
                <AwesomeIcon
                  name={faSearch}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
              </div>
            </div>

            <div className="max-h-[60vh] overflow-y-auto scrollbar-light dark:scrollbar">
              <div className="columns-1 md:columns-2 gap-4">
                {filteredSections.map((section) => (
                  <div key={section.title} className="break-inside-avoid mb-4">
                    {/* Section Header */}
                    <div className="px-4 py-3 bg-gradient-to-r from-gray-800 to-gray-700 rounded-t-lg flex items-center mb-2 shadow-lg">
                      <div className="p-2 bg-teal-500/10 rounded-lg mr-3">
                        <AwesomeIcon
                          name={iconMap[section.icon]}
                          className="text-teal-400"
                        />
                      </div>
                      <span className="font-medium text-white">{section.title}</span>
                    </div>

                    {/* Section Content */}
                    <div className="space-y-2">
                      {section.prompts.map((prompt) => (
                        <div
                          key={prompt.name}
                          className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg overflow-hidden hover:from-gray-750 hover:to-gray-800 transition-all duration-200 shadow-lg"
                        >
                          <div className="flex">
                            <div className="flex-1 p-4">
                              <h4 className="font-medium text-white mb-2 flex items-center">
                                <span className="bg-teal-500/10 text-teal-400 text-xs px-2 py-1 rounded mr-2">
                                  Prompt
                                </span>
                                {prompt.name}
                              </h4>
                              <div className="mt-2">
                                <p className="text-gray-300 text-sm font-mono bg-gray-900/50 p-3 rounded border border-gray-700/50 backdrop-blur-sm">
                                  {prompt.content}
                                </p>
                              </div>
                            </div>
                            <div className="flex flex-col justify-center items-center p-2 border-l border-gray-700/50 bg-gray-900/50">
                              <button
                                onClick={() => handleCopyPrompt(prompt.content)}
                                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-full transition-colors mb-2 hover:scale-110 transform duration-200"
                                title="Copy to clipboard"
                              >
                                <AwesomeIcon name={faCopy} />
                              </button>
                              <button
                                onClick={() => handleUsePrompt(prompt.content)}
                                className="p-2 text-teal-400 hover:text-teal-300 hover:bg-teal-900/50 rounded-full transition-colors hover:scale-110 transform duration-200"
                                title="Use in chat"
                              >
                                <AwesomeIcon name={faPaperPlane} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptsModal;
